import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getUrlParams } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "./Theme.css";
import "./Medicate.css";

const TAB_LABELS = {
  0: "Skin Diary",
  1: "Medication Adherence",
  13: "Side Effects",
  5: "Skin Mood",
  6: "Emotions",
  7: "Flare-up",
  8: "Self Stats",
  2: "DLQI",
  3: "PSQI",
  4: "EASI",
  10: "BSA",
  9: "Locker",
  11: "WFIRS-S",
  14: "Patient Information",
  15: "All Consultation Dates",
};

const ScoreTable = ({ rows, noDataMsg }) => {
  if (!rows || rows.length === 0) return <p className="med-no-data">{noDataMsg}</p>;
  const hasReport = rows.some((r) => r.reportUrl);
  return (
    <div className="med-table-wrap">
      <table className="med-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Score</th>
            {hasReport && <th>Report</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((entry, i) => (
            <tr key={i}>
              <td>{entry.logDate}</td>
              <td>{entry.score}</td>
              {hasReport && (
                <td>
                  <a href={entry.reportUrl} target="_blank" rel="noopener noreferrer" className="med-report-link">
                    View Report
                  </a>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const IframeTab = ({ src }) => {
  if (!src) return <p className="med-no-data">No data available.</p>;
  return (
    <div className="med-iframe-wrap">
      <iframe src={src} title="Tab Content" className="med-iframe" />
    </div>
  );
};

const Medicate = () => {
  const { userId } = useParams();
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();

  const [activeTab, setActiveTab] = useState(0);
  const [userData, setUserData] = useState(null);
  const [flags, setFlags] = useState({
    easiDisable: "Y",
    medicationDisable: "Y",
    sideEffectsDisable: "Y",
    skinModeDisable: "Y",
    skinDiaryDisable: "Y",
    emotionsDisable: "Y",
    flareupDisable: "Y",
    selfStatsDisable: "Y",
    dlqiDisable: "Y",
    psqiDisable: "Y",
    bsaDisable: "Y",
    lockerDisable: "Y",
    wfirssDisable: "Y",
    patientInformation: "Y",
    allConsultationDates: "Y",
  });

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  useEffect(() => {
    lifestyleApi.getSkinDetails({ goqiiUserId: userId })
      .then((res) => {
        if (String(res.data.code) === "200") {
          setUserData(res.data);
          setFlags({
            easiDisable: res.data.easiDisable || "Y",
            medicationDisable: res.data.medicationDisable || "Y",
            sideEffectsDisable: res.data.sideEffectsDisable || "Y",
            skinModeDisable: res.data.skinModeDisable || "Y",
            skinDiaryDisable: res.data.skinDiaryDisable || "Y",
            emotionsDisable: res.data.emotionsDisable || "Y",
            flareupDisable: res.data.flareupDisable || "Y",
            selfStatsDisable: res.data.selfStatsDisable || "Y",
            dlqiDisable: res.data.dlqiDisable || "Y",
            psqiDisable: res.data.psqiDisable || "Y",
            bsaDisable: res.data.bsaDisable || "Y",
            lockerDisable: res.data.lockerDisable || "Y",
            wfirssDisable: res.data.wfirssDisable || "Y",
            patientInformation: "N",
            allConsultationDates: res.data.allConsultationDates || "Y",
          });
          setActiveTab(res.data.activeTab ?? 0);
        }
      })
      .catch((err) => console.error("getSkinDetails:", err));
  }, [userId]);

  if (!userData) return <div className="med-loading">Loading...</div>;

  const tabs = [
    flags.skinDiaryDisable === "N" && 0,
    flags.medicationDisable === "N" && 1,
    flags.sideEffectsDisable === "N" && 13,
    flags.skinModeDisable === "N" && 5,
    flags.emotionsDisable === "N" && 6,
    flags.flareupDisable === "N" && 7,
    flags.selfStatsDisable === "N" && 8,
    flags.dlqiDisable === "N" && 2,
    flags.psqiDisable === "N" && 3,
    flags.easiDisable === "N" && 4,
    flags.bsaDisable === "N" && 10,
    flags.lockerDisable === "N" && 9,
    flags.wfirssDisable === "N" && 11,
    flags.patientInformation === "N" && 14,
    flags.allConsultationDates === "N" && 15,
  ].filter((t) => t !== false);

  const data = userData.data || {};

  return (
    <div className="med-page">
      <div className="med-tabs-scroll">
        {tabs.map((tabId) => (
          <button
            key={tabId}
            className={`med-tab-btn ${activeTab === tabId ? "med-tab-btn--active" : ""}`}
            onClick={() => setActiveTab(tabId)}
          >
            {TAB_LABELS[tabId]}
            {tabId === 13 && data.tab13 && <span className="med-dot" />}
          </button>
        ))}
      </div>

      <div className="med-content">
        {activeTab === 0 && (
          <div className="med-gallery">
            {!data.skinImages || data.skinImages.length === 0 ? (
              <p className="med-no-data">No images found.</p>
            ) : (
              <Swiper slidesPerView="auto" spaceBetween={16} className="med-swiper">
                {data.skinImages.map((group, i) => (
                  <SwiperSlide key={i} className="med-swiper-slide">
                    <h2 className="med-gallery-title">{group.name}</h2>
                    <div className="med-gallery-imgs">
                      {group.images.map((img, j) => (
                        <img key={j} src={img.attachmentUrl} alt={`${group.name} ${j + 1}`} className="med-gallery-img" />
                      ))}
                    </div>
                    <p className="med-gallery-date">{group.date} {group.time}</p>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>
        )}
        {activeTab === 1 && <IframeTab src={data.tab2} />}
        {activeTab === 13 && <IframeTab src={data.tab13} />}
        {activeTab === 2 && <ScoreTable rows={data.dlqi} noDataMsg="No DLQI data available." />}
        {activeTab === 3 && <ScoreTable rows={data.psqi} noDataMsg="No PSQI data available." />}
        {activeTab === 4 && <ScoreTable rows={data.easi} noDataMsg="No EASI data available." />}
        {activeTab === 5 && <IframeTab src={data.tab5} />}
        {activeTab === 6 && <IframeTab src={data.tab6} />}
        {activeTab === 7 && <IframeTab src={data.tab7} />}
        {activeTab === 8 && <IframeTab src={data.tab8} />}
        {activeTab === 9 && (
          <div className="med-table-wrap">
            {!data.locker || data.locker.length === 0 ? (
              <p className="med-no-data">No Locker data available.</p>
            ) : (
              <table className="med-table">
                <thead><tr><th>Date</th><th>Report</th></tr></thead>
                <tbody>
                  {data.locker.map((entry, i) => (
                    <tr key={i}>
                      <td>{entry.logDate}</td>
                      <td><a href={entry.reportUrl} target="_blank" rel="noopener noreferrer" className="med-report-link">View Report</a></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {activeTab === 10 && <ScoreTable rows={data.bsa} noDataMsg="No BSA data available." />}
        {activeTab === 11 && <ScoreTable rows={data.wfirss} noDataMsg="No WFIRS-S data available." />}
        {activeTab === 14 && <IframeTab src={data.tab14} />}
        {activeTab === 15 && <IframeTab src={data.tab15} />}
      </div>
    </div>
  );
};

export default Medicate;
